import React, { useCallback } from 'react';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { getIssueErrorAttributes } from '@jira/issue__issue-view-errors/src/getIssueLimitErrorField.tsx';
import { truncateStackTrace } from '@jira/issue__issue-view-errors/src/truncateStackTrace.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { fg } from '@jira/platform__feature-gating';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { getTenantAttributes } from './getTenantAttributes.tsx';
import type { Props } from './UiProps.types.tsx';

export const IssueCommonErrorBoundary = ({
	children,
	fallback,
	fireErrorEventWhenNetworkError,
	getErrorAttributes,
	attributes,
	...otherProps
}: Props): React.JSX.Element => {
	// TODO: understand what more common attributes can be common in all of issue domain
	// TODO: understand what is more common for issue view and create and transition.
	const accountIdLoggedInUser = useAccountId();
	const getAttributes = useCallback(
		(error: Error): AnalyticsAttributes => {
			const attributesPassed = typeof attributes === 'function' ? attributes(error) : attributes;
			const tenantAttributes = getTenantAttributes();
			const { errorAttributes } = getIssueErrorAttributes(error, accountIdLoggedInUser);

			return {
				...attributesPassed,
				...tenantAttributes,
				...errorAttributes,
			};
		},
		[attributes, accountIdLoggedInUser],
	);
	const isFallbackString = typeof fallback === 'string';

	const fallbackMethod = useCallback(
		(errorProps: { error: Error }) => {
			const { errorAttributes } = getIssueErrorAttributes(errorProps.error, accountIdLoggedInUser);

			if (
				errorAttributes.statusCode &&
				errorAttributes.statusCode >= 400 &&
				errorAttributes.statusCode < 500
			) {
				fireErrorAnalytics({
					error: errorProps.error,
					meta: {
						id: 'issue.issue-view.viewIssue',
						teamName: 'Bento',
					},
					attributes: {
						...attributes,
						...getErrorAttributes?.(errorProps.error),
						statusCode: errorAttributes.statusCode,
						traceId: errorAttributes.traceId || undefined,
						...(fg('bento-1705-add-stack-trace') && {
							errorStack:
								errorAttributes.errorStack ||
								truncateStackTrace(errorProps.error.stack) ||
								undefined,
						}),
					},
				});
				return !isFallbackString && fallback?.(errorProps);
			}
		},
		[accountIdLoggedInUser, attributes, fallback, getErrorAttributes, isFallbackString],
	);
	const fallbackAbstracted =
		fireErrorEventWhenNetworkError && !isFallbackString ? fallbackMethod : fallback;
	return (
		<JSErrorBoundary {...otherProps} attributes={getAttributes} fallback={fallbackAbstracted}>
			{children}
		</JSErrorBoundary>
	);
};
